import React, { useState, useEffect } from 'react';
import { Eye, EyeOff, Loader2 } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Separator } from '@/components/ui/separator';
import { useAuth } from '../contexts/AuthContext';
import { API_BASE_URL } from '../services/api/client';

// Samme tekst som serverens generiske svar (backend authUtils.INNLOGGING_FEILET).
const INNLOGGING_FEILET = 'Feil e-post eller passord.';

const Login: React.FC = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [searchParams] = useSearchParams();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const errorParam = searchParams.get('error');
    if (errorParam === 'oauth_failed') {
      setError('Innloggingen feilet. Prøv igjen eller logg inn med e-post og passord.');
    }
  }, [searchParams]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(email, password);
      navigate('/dashboard');
    } catch (err: any) {
      if (err.response?.status === 401) {
        setError(err.response.data?.error || INNLOGGING_FEILET);
      } else if (err.response?.status === 429) {
        setError(err.response.data.error || 'For mange påloggingsforsøk. Prøv igjen senere');
      } else {
        setError('En feil oppstod. Vennligst prøv igjen.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    window.location.href = `${API_BASE_URL}/auth/google`;
  };

  const handleFacebookLogin = () => {
    window.location.href = `${API_BASE_URL}/auth/facebook`;
  };

  const fieldClass =
    'h-11 rounded-xl border-transparent bg-[#1a5252] text-white placeholder:text-white/40 focus-visible:ring-white/40 focus-visible:ring-offset-0';

  return (
    <div className="flex min-h-screen items-center justify-center bg-[#0d4249]">
      <div className="w-full max-w-xs px-4">
        <div className="mb-10 flex flex-col items-center">
          <img src="/logo.png" alt="Voluplan logo" className="mb-4 h-[110px] w-[110px] object-contain" />
          <h1 className="text-2xl font-extrabold tracking-[0.15em] text-white sm:text-3xl">
            VOLUPLAN
          </h1>
        </div>

        {error && (
          <Alert variant="destructive" className="mb-6 rounded-lg">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
          <p className="mb-1 ml-0.5 text-sm text-white/70">Email</p>
          <Input
            type="email"
            placeholder="din@email.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            autoComplete="email"
            autoFocus
            className={`mb-4 ${fieldClass}`}
          />

          <p className="mb-1 ml-0.5 text-sm text-white/70">Passord</p>
          <div className="relative mb-2">
            <Input
              type={showPassword ? 'text' : 'password'}
              placeholder="Skriv inn passord"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoComplete="current-password"
              className={`pr-10 ${fieldClass}`}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-white/60 hover:text-white"
              aria-label={showPassword ? 'Skjul passord' : 'Vis passord'}
            >
              {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
            </button>
          </div>

          <div className="mb-6 text-right">
            <button
              type="button"
              onClick={() => navigate('/forgot-password')}
              className="text-sm text-white/60 hover:text-white"
            >
              Glemt passord?
            </button>
          </div>

          <Button
            type="submit"
            disabled={loading}
            className="h-12 w-full rounded-xl bg-[#F5A623] text-base font-bold tracking-wide text-white shadow-none hover:bg-[#e0951f] disabled:bg-[#F5A623]/50"
          >
            {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : 'Logg inn'}
          </Button>
        </form>

        <p className="mt-4 text-center text-sm text-white/60">
          Ny bruker, eller har du bare brukt Google/Facebook? Bruk «Glemt passord?» for å lage et passord.
        </p>

        <div className="my-6 flex items-center gap-3">
          <Separator className="flex-1 bg-white/20" />
          <span className="text-sm text-white/40">ELLER</span>
          <Separator className="flex-1 bg-white/20" />
        </div>

        <div className="flex gap-4">
          <Button
            type="button"
            variant="outline"
            onClick={handleGoogleLogin}
            className="h-11 w-full flex-1 rounded-xl border-[#DB4437]/70 bg-transparent text-[#ff6b5b] hover:border-[#DB4437] hover:bg-[#DB4437]/10 hover:text-[#ff6b5b]"
          >
            Google
          </Button>

          <Button
            type="button"
            variant="outline"
            onClick={handleFacebookLogin}
            className="h-11 w-full flex-1 rounded-xl border-[#4267B2]/70 bg-transparent text-[#7b9fd4] hover:border-[#4267B2] hover:bg-[#4267B2]/10 hover:text-[#7b9fd4]"
          >
            Facebook
          </Button>
        </div>

        <p className="mt-8 text-center text-sm text-white/40">
          Har du ikke tilgang?{' '}
          <button type="button" className="text-white/70 hover:text-white">
            Kontakt administrator
          </button>
        </p>
      </div>
    </div>
  );
};

export default Login;
